/**
 * It is a common function to create a temporary url to download a file.
 * The downloadUrlLink function takes a URL, an extension, and a filename as arguments.
 * It creates an anchor element with the href set to the url argument and download attribute set to `${filename}.${extension}`.
 * The function then clicks on that anchor element which triggers the browser's default behavior of downloading files.
 * Finally, it removes that anchor element from the DOM.

 *
 * @param url Specify the location of the file to be downloaded
 * @param extension Determine the file type of the downloaded image
 * @param filename Set the name of the downloaded file
 *
 * @return A link that downloads the file
 */
export function downloadUrlLink(url, extension, filename = "chart") {
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.${extension}`;
  a.click();
  a.remove();
}

// First character that makes a spreadsheet evaluate the cell as a formula.
const CSV_FORMULA_START = /^[=+\-@\t\r]/;

// Operators that give away a real formula inside a cell starting like a negative number.
const CSV_FORMULA_CHARS = /[=+|!]/;

// True for values like "-1,234.56 km"; false for "-2+3+cmd|...".
function isPlainNumericCell(str) {
  return /^-?\d/.test(str) && !CSV_FORMULA_CHARS.test(str.slice(1));
}

/**
 * Turns a value into a CSV-safe field: RFC 4180 quoting, plus a leading
 * `'` on formula-like cells. Quoting alone does not protect: Excel and
 * Sheets unquote first and evaluate afterwards. Plain numbers are left alone.
 *
 * @param {*} value
 * @returns {string} The escaped field
 */
export function escapeCsvCell(value) {
  let str = String(value ?? "");

  if (CSV_FORMULA_START.test(str) && !isPlainNumericCell(str)) {
    str = `'${str}`;
  }

  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

// Helps Excel on Windows pick UTF-8 instead of the system code page.
const UTF8_BOM = "\uFEFF";

/**
 * Serializes a table to CSV (CRLF rows, UTF-8 BOM) and starts the download.
 *
 * @param {Array<Array>} rows One array per row, the first one usually the header
 * @param {string} filename Without extension
 */
export function downloadCsvRows(rows, filename = "chart") {
  if (!rows || rows.length === 0) return;

  const csv = rows.map(row => row.map(escapeCsvCell).join(",")).join("\r\n");

  const blob = new Blob([UTF8_BOM + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  downloadUrlLink(url, "csv", filename);
  URL.revokeObjectURL(url);
}

/**
 * The getPrettyFileSize function takes a number of bytes and returns a string with the file size in a human-readable format.
 *
 * @param {number} bytes - The number of bytes to convert to a human-readable format
 * @returns {string} A string with the file size in a human-readable format
 */
export function getPrettyFileSize(bytes) {
  const units = ["B", "KB", "MB", "GB"];
  const k = 1024;

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = (bytes / Math.pow(k, i)).toFixed(0);

  return `${i === 0 ? bytes : size} ${units[i]}`;
}

// Mirrors ScenarioPath.name's backend max_length.
export const MAX_PATH_NAME_LENGTH = 50;

const PATH_NAME_INVISIBLE_CHARS =
  /[\u0000-\u001F\u007F\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g; // eslint-disable-line no-control-regex

/**
 * Sanitizes a path display name. Unlike sanitizeFilename, this preserves
 * filename-valid punctuation and spaces because it is not used as a
 * filesystem filename.
 *
 * @param {*} value Candidate path name
 *
 * @returns {string} Sanitized path name, possibly empty
 */
export function sanitizePathName(value) {
  return String(value ?? "")
    .replace(PATH_NAME_INVISIBLE_CHARS, "")
    .normalize("NFC")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_PATH_NAME_LENGTH)
    .trim();
}

/**
 * Derives a path display name from a file's basename and removes only its
 * final extension. This intentionally does not use sanitizeFilename, which
 * enforces a filesystem filename contract rather than a display-name contract.
 *
 * @param {*} file File-like object containing a name
 *
 * @returns {string} Derived path name, possibly empty
 */
export function deriveNameFromFile(file) {
  const basename = String(file?.name ?? "")
    .split(/[/\\]/)
    .pop();
  const extensionIndex = basename.lastIndexOf(".");
  const name =
    extensionIndex > 0 ? basename.slice(0, extensionIndex) : basename;

  return sanitizePathName(name);
}

/**
 * Extracts the basename used as an uploaded object's storage key.
 *
 * @param {*} filename Candidate upload filename
 *
 * @returns {string} Basename
 */
export function uploadBasename(filename) {
  return String(filename ?? "")
    .split(/[/\\]/)
    .pop();
}

/**
 * Converts an upload filename's basename to one encoded URL path segment.
 * The server must still validate the basename after URL decoding.
 *
 * @param {*} filename Candidate upload filename
 *
 * @returns {string} Encoded basename
 */
export function encodeUploadFilename(filename) {
  return encodeURIComponent(uploadBasename(filename));
}

/**
 * Sanitize a filename by removing or replacing invalid characters.
 * Null/undefined → `""` (callers should fall back, e.g. `|| "chart"`).
 * @param {*} filename
 * @returns {string}
 */
export function sanitizeFilename(filename) {
  return String(filename ?? "")
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, "") // eslint-disable-line no-control-regex
    .replace(/\s+/g, "_")
    .substring(0, 255);
}
